import { useMemo } from 'react';
import { usePage } from '@inertiajs/react';

import {
  resolveDraftContentModel,
  resolvePreviewContentModel,
} from '@/components/page-sections/metadata-controls/content-model-page';
import { usePageSectionContext } from '@/components/page-sections/page-section-context';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { BasePageProps, Page, PageContentModel } from '@/types/page';

export interface ResolvedContentModelResult {
  contentModel: PageContentModel | null;
  isTemplatePreview: boolean;
}

export function useActiveEditorPage(): Page | null {
  const inertiaPage = usePage<{ page?: BasePageProps['page']; [key: string]: unknown }>().props
    .page;
  const storePage = usePageEditorStore((state) => state.page);
  const historyMode = usePage<{ historyMode?: boolean; [key: string]: unknown }>().props
    .historyMode;

  if (!inertiaPage && !storePage) return null;
  if (historyMode) return inertiaPage ?? null;

  if (storePage && inertiaPage && storePage.id === inertiaPage.id) {
    return storePage;
  }

  return inertiaPage ?? storePage ?? null;
}

export function useResolvedContentModel(sectionId?: string): ResolvedContentModelResult {
  const sectionContext = usePageSectionContext();
  const resolvedSectionId = sectionId ?? sectionContext?.sectionId;
  const activePage = useActiveEditorPage();
  const isPageEditor = useIsPageEditor();
  const renderMode = sectionContext?.renderMode;
  const shouldUseDraftContentModel = isPageEditor || renderMode === 'template_preview';

  // Memoize so resolve* don't hand a fresh contentModel/authors/topics reference to
  // consumers on every render, which would loop their identity-keyed effects.
  const contentModel = useMemo(() => {
    if (!activePage) return null;
    return shouldUseDraftContentModel
      ? resolveDraftContentModel(activePage, resolvedSectionId)
      : resolvePreviewContentModel(activePage);
  }, [activePage, resolvedSectionId, shouldUseDraftContentModel]);

  return {
    contentModel,
    isTemplatePreview: renderMode === 'template_preview',
  };
}
